import React, { useState, useEffect } from 'react';
import { Plus, User, Calendar, Clock, CheckCircle2, AlertCircle, Check, Filter, Clock4, ChevronRight, X } from 'lucide-react';
import CreateTaskModal from '../../components/tasks/CreateTaskModal';
import { getResources, Resource } from '../../lib/resources';
import { Task as TaskType } from '../../lib/tasks';
import { useBusiness } from '../../contexts/BusinessContext';
import Task from '../../components/tasks/Task';
import RecurringTasksOverview from '../../components/tasks/RecurringTasksOverview';
import { format } from 'date-fns';
import { useTasks } from '../../contexts/TasksContext';
import FloatingActionButton from '../../components/FloatingActionButton';

type TaskStatus = 'pending' | 'completed' | 'up_coming' | 'all';

interface TasksProps {
  onTaskCreated?: (task: TaskType) => void;
}

export default function Tasks({ onTaskCreated }: TasksProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [resources, setResources] = useState<Resource[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<TaskStatus>('pending');
  const [latestTask, setLatestTask] = useState<TaskType | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const { selectedBusiness } = useBusiness();
  const { tasks, isLoading, error, updateTask, selectedResource, setSelectedResource } = useTasks();

  useEffect(() => {
    const loadResources = async () => {
      if (selectedBusiness?.id) {
        const resourcesList = await getResources(selectedBusiness.id);
        setResources(resourcesList);
      }
    };
    loadResources();
  }, [selectedBusiness?.id]);

  // Filter active resources
  const activeResources = resources.filter(resource => resource.status === 'active');

  // Filter tasks based on selected status
  const filteredTasks = tasks.filter(task => {
    if (selectedStatus === 'all') return true;
    return task.status === selectedStatus;
  });

  // Sort tasks by creation date (newest first)
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return dateB - dateA;
  });

  // Get upcoming tasks (tasks with future start dates)
  const upcomingTasks = tasks.filter(task => {
    const startDate = new Date(task.start_date);
    return startDate > new Date() && task.status !== 'completed';
  }).sort((a, b) => {
    const dateA = new Date(a.start_date).getTime();
    const dateB = new Date(b.start_date).getTime();
    return dateA - dateB;
  });

  const getResourceName = (resourceId: string | null | undefined): string => {
    if (!resourceId) return 'Unassigned';
    const resource = resources.find(r => r.id === resourceId);
    return resource ? `${resource.first_name} ${resource.last_name}` : 'Unknown';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'pending':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'up_coming':
        return <Clock4 className="h-5 w-5 text-blue-500" />;
      default:
        return null;
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      await updateTask(taskId, 'completed');
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  const handleTaskCreated = (newTask: TaskType) => {
    setLatestTask(newTask);
    onTaskCreated?.(newTask);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6">
        <div className={`transition-all duration-300 ${!isSidebarCollapsed ? 'sm:pr-[21rem]' : ''}`}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 py-4 sm:py-6">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Tasks</h1>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center flex-grow">
              <div className="flex items-center gap-2 min-w-0">
                <User className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <select
                  value={selectedResource || 'all'}
                  onChange={(e) => setSelectedResource(e.target.value === 'all' ? null : e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                >
                  <option value="all">All Resources</option>
                  {activeResources.map((resource) => (
                    <option key={resource.id} value={resource.id}>
                      {resource.first_name} {resource.last_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2 min-w-0">
                <Filter className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value as TaskStatus)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                >
                  <option value="all">All Tasks</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6">
            {/* Main Tasks Section */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-6">
                {isLoading ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Loading tasks...</p>
                  </div>
                ) : sortedTasks.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No tasks found</p>
                  </div>
                ) : (
                  sortedTasks.map((task) => (
                    <Task
                      key={task.id}
                      task={task}
                      onStatusChange={updateTask}
                      getResourceName={getResourceName}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Weekly Recurring Tasks Section */}
            <div className="bg-white rounded-lg shadow">
              <div className="flex items-center justify-between p-4 border-b">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-500" />
                  <h2 className="text-lg font-medium text-gray-900">Weekly Recurring Tasks</h2>
                </div>
                <button
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <ChevronRight className={`h-5 w-5 transform transition-transform ${!isSidebarCollapsed ? 'rotate-180' : ''}`} />
                </button>
              </div>
              <RecurringTasksOverview />
            </div>
          </div>
        </div>
      </div>

      {/* Right Sidebar */}
      <div className={`fixed right-0 top-0 h-full w-[20rem] bg-white border-l border-gray-200 shadow-lg transform transition-transform duration-300 ease-in-out ${isSidebarCollapsed ? 'translate-x-full' : 'translate-x-0'}`}>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">Upcoming Tasks</h3>
          <button
            onClick={() => setIsSidebarCollapsed(true)}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {upcomingTasks.length === 0 ? (
            <p className="text-center text-gray-500 py-4">No upcoming tasks</p>
          ) : (
            upcomingTasks.map((task) => (
              <div 
                key={task.id}
                className="bg-white rounded-lg border border-gray-200 p-3 hover:border-blue-300 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900 truncate">{task.title}</h4>
                    {task.description && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{task.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="h-3.5 w-3.5 mr-1" />
                        {format(new Date(task.start_date), 'MMM d, h:mm a')}
                      </div>
                      <div className={`px-1.5 py-0.5 text-xs font-medium rounded-full ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </div>
                    </div>
                    <div className="flex items-center text-xs text-gray-500 mt-1">
                      <User className="h-3.5 w-3.5 mr-1" />
                      {getResourceName(task.resource_id)}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Sidebar Toggle Button */}
      <button
        onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        className={`fixed right-4 top-4 z-50 p-2 rounded-full bg-white shadow-lg hover:bg-gray-50 transition-colors ${isSidebarCollapsed ? '' : 'hidden'}`}
      >
        <ChevronRight className="h-5 w-5 text-gray-600" />
      </button>

      <CreateTaskModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        resources={resources}
      />

      <FloatingActionButton 
        permissionLevel="manage"
        onSuccess={async () => {
          setIsCreateModalOpen(true);
          return Promise.resolve();
        }}
      />
    </div>
  );
} 